import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Youtube, Mail, Instagram, ChevronDown } from "lucide-react";
import { SITE } from "../lib/site";
import BookingButton from "./BookingButton";

/** Structure éditoriale du site — modifiée en 07/2026 pour SEO local Pantin. */
const NAV = [
  { to: "/", label: "Accueil" },
  {
    to: "/hypnose",
    label: "L'Hypnose",
    children: [
      { to: "/hypnose/ericksonienne", label: "Hypnose ericksonienne" },
      { to: "/hypnose/spirituelle", label: "Hypnose spirituelle" },
      { to: "/hypnose/regressive", label: "Hypnose régressive" },
      { to: "/hypnose/questions-frequentes", label: "Questions fréquentes" },
    ],
  },
  { to: "/pourquoi-consulter", label: "Pourquoi consulter\u00A0?" },
  {
    to: "/votre-seance",
    label: "Votre séance",
    children: [
      { to: "/votre-seance/deroulement", label: "Déroulement d'une séance" },
      { to: "/votre-seance/en-ligne", label: "Séance en ligne" },
      { to: "/votre-seance/cabinet-pantin", label: "Le cabinet à Pantin" },
      { to: "/votre-seance/tarifs", label: "Tarifs" },
      { to: "/votre-seance/a-propos", label: "À propos de Valentine DL" },
      { to: "/votre-seance/temoignages", label: "Avis & témoignages" },
    ],
  },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

function testIdFromPath(p) {
  return p === "/" ? "home" : p.replace(/^\//, "").replace(/\//g, "-");
}

function Seo({ title, description, image, url, canonical }) {
  const fullTitle = title ? `${title} | Parfaits Imparfaits` : "Parfaits Imparfaits – Valentine DL, Hypnose régressive et spirituelle à Pantin";
  const desc =
    description ||
    "Valentine DL, praticienne en hypnose régressive et spirituelle à Pantin (Métro Église de Pantin, 15 min de Paris) et en ligne, en visio. Ouverture de l'hypnose ericksonienne à partir de septembre 2026.";
  const img = image || "https://images.unsplash.com/photo-1761027621746-8ecd3ba01334?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";
  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={img} />
      {url && <meta property="og:url" content={url} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
    </>
  );
}

export { Seo };

function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const loc = useLocation();
  const isActiveParent = loc.pathname === item.to || loc.pathname.startsWith(item.to + "/");
  const openNow = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    timer.current = setTimeout(() => setOpen(false), 120);
  };
  return (
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      data-testid={`nav-group-${testIdFromPath(item.to)}`}
    >
      <NavLink
        to={item.to}
        data-testid={`nav-${testIdFromPath(item.to)}`}
        className={`text-sm tracking-wide flex items-center gap-1 transition-colors duration-200 ${
          isActiveParent ? "text-sand-900" : "text-sand-700 hover:text-sand-900"
        }`}
      >
        {item.label}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>
      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-40"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
        >
          <div className="glass rounded-2xl border border-sand-300/50 shadow-xl py-3 min-w-[280px]">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${testIdFromPath(c.to)}`}
                className={({ isActive }) =>
                  `block px-5 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "text-sand-900 bg-sand-200/40"
                      : "text-sand-700 hover:text-sand-900 hover:bg-sand-200/30"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileGroup({ item }) {
  const loc = useLocation();
  const [open, setOpen] = useState(loc.pathname.startsWith(item.to));
  return (
    <div className="border-b border-sand-300/40 last:border-0" data-testid={`mobile-nav-group-${testIdFromPath(item.to)}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full py-3 flex items-center justify-between text-base text-sand-800"
      >
        <span>{item.label}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-3 pl-3 flex flex-col">
          <NavLink
            to={item.to}
            data-testid={`mobile-nav-${testIdFromPath(item.to)}`}
            className={({ isActive }) =>
              `py-2 text-sm ${isActive ? "text-sand-900 font-medium" : "text-sand-600"}`
            }
          >
            Voir la rubrique
          </NavLink>
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              data-testid={`mobile-nav-${testIdFromPath(c.to)}`}
              className={({ isActive }) =>
                `py-2 text-sm ${isActive ? "text-sand-900 font-medium" : "text-sand-600"}`
              }
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group" aria-label="Parfaits Imparfaits — accueil">
          <img
            src="/img/logo-dark.webp"
            alt="Parfaits Imparfaits"
            width="180"
            height="71"
            className="h-10 sm:h-12 w-auto"
            loading="eager"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map((item) =>
            item.children ? (
              <NavDropdown key={item.to} item={item} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${testIdFromPath(item.to)}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors duration-200 ${
                    isActive ? "text-sand-900" : "text-sand-700 hover:text-sand-900"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
          <BookingButton
            testid="cta-rdv-nav"
            className="!py-2 !px-5 text-sm"
          >
            Réserver
          </BookingButton>
        </nav>

        <button
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          data-testid="menu-toggle"
          className="lg:hidden p-2 text-sand-900"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-sand-300/40" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((item) =>
              item.children ? (
                <MobileGroup key={item.to} item={item} />
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-${testIdFromPath(item.to)}`}
                  className={({ isActive }) =>
                    `py-3 text-base ${isActive ? "text-sand-900" : "text-sand-700"}`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
            <BookingButton testid="cta-rdv-mobile" className="w-full mt-4">
              Réserver une séance
            </BookingButton>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-sand-900 text-sand-100 mt-32" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-1">
          <h3 className="text-3xl font-serif mb-4">Parfaits Imparfaits</h3>
          <p className="text-sand-300 leading-relaxed text-sm max-w-sm">
            Valentine DL — praticienne en hypnose régressive et spirituelle, à Pantin et en ligne (visio).
            <span className="block mt-2 text-sand-400 italic">
              Hypnose ericksonienne à partir de septembre 2026.
            </span>
          </p>
          <p className="mt-6 italic text-sand-400 font-serif text-lg">
            « Comprendre pour avancer, transformer pour se libérer »
          </p>
        </div>

        <div>
          <p className="eyebrow !text-sand-400 mb-5">Découvrir</p>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/" data-testid="footer-nav-home" className="text-sand-200 hover:text-white transition-colors">
                Accueil
              </Link>
            </li>
            <li>
              <Link to="/hypnose" data-testid="footer-nav-hypnose" className="text-sand-200 hover:text-white transition-colors">
                L'Hypnose
              </Link>
            </li>
            <li>
              <Link to="/pourquoi-consulter" data-testid="footer-nav-pourquoi-consulter" className="text-sand-200 hover:text-white transition-colors">
                Pourquoi consulter&nbsp;?
              </Link>
            </li>
            <li>
              <Link to="/hypnose/questions-frequentes" data-testid="footer-nav-faq" className="text-sand-200 hover:text-white transition-colors">
                FAQ
              </Link>
            </li>
            <li>
              <Link to="/blog" data-testid="footer-nav-blog" className="text-sand-200 hover:text-white transition-colors">
                Blog
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="eyebrow !text-sand-400 mb-5">Votre séance</p>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/votre-seance/cabinet-pantin" data-testid="footer-nav-cabinet" className="text-sand-200 hover:text-white transition-colors">
                Cabinet à Pantin
              </Link>
            </li>
            <li>
              <Link to="/votre-seance/en-ligne" data-testid="footer-nav-en-ligne" className="text-sand-200 hover:text-white transition-colors">
                Séances en ligne
              </Link>
            </li>
            <li>
              <Link to="/votre-seance/deroulement" data-testid="footer-nav-deroulement" className="text-sand-200 hover:text-white transition-colors">
                Déroulement
              </Link>
            </li>
            <li>
              <Link to="/votre-seance/tarifs" data-testid="footer-nav-tarifs" className="text-sand-200 hover:text-white transition-colors">
                Tarifs
              </Link>
            </li>
            <li>
              <Link to="/votre-seance/temoignages" data-testid="footer-nav-temoignages" className="text-sand-200 hover:text-white transition-colors">
                Témoignages
              </Link>
            </li>
            <li>
              <Link to="/votre-seance/a-propos" data-testid="footer-nav-a-propos" className="text-sand-200 hover:text-white transition-colors">
                À propos
              </Link>
            </li>
            <li>
              <Link to="/reserver" data-testid="footer-nav-reserver" className="text-sand-200 hover:text-white transition-colors">
                Réserver
              </Link>
            </li>
            <li>
              <Link to="/contact" data-testid="footer-nav-contact" className="text-sand-200 hover:text-white transition-colors">
                Contact
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="eyebrow !text-sand-400 mb-5">Contact</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3">
              <Mail size={16} className="text-sand-500" />
              <a
                href="mailto:v.parfaitsimparfaits@gmail.com"
                data-testid="footer-email"
                className="hover:text-white break-all"
              >
                v.parfaitsimparfaits@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Youtube size={16} className="text-sand-500" />
              <a
                href={SITE.youtube || "https://www.youtube.com/@ParfaitsImparfaits"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                className="hover:text-white"
              >
                @ParfaitsImparfaits
              </a>
            </li>
            {SITE.instagram && (
              <li className="flex items-center gap-3">
                <Instagram size={16} className="text-sand-500" />
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  className="hover:text-white"
                >
                  @parfaits_imparfaits
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="border-t border-sand-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-sand-400">
          <span>© {new Date().getFullYear()} Parfaits Imparfaits — Tous droits réservés.</span>
          <div className="flex flex-wrap items-center gap-5">
            <Link to="/mentions-legales" data-testid="footer-legal" className="hover:text-white">Mentions légales</Link>
            <Link to="/politique-de-confidentialite" data-testid="footer-confidentialite" className="hover:text-white">Politique de confidentialité</Link>
            <span>Présentiel à Pantin · Séances en ligne (visio)</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-sand-100 text-sand-900">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
